import { useCallback, useEffect, useRef } from 'react';

import { useAiBuilderChatRestoreDecision } from '../stores/aiBuilderChatRestoreDecision';
import { createAiBuilderDraftInstanceId } from '../stores/aiBuilderTurnRequests';
import { type AiBuilderChatState } from '../stores/types';
import {
  type AiBuilderChatRestoreReadResult,
  type AiBuilderChatRestoreSnapshot,
  applyAiBuilderChatRestoreSnapshot,
  clearAiBuilderChatRestoreSnapshot,
  consumeAiBuilderChatRestoreSnapshot,
  readAiBuilderChatRestoreSnapshot,
  resolveAiBuilderChatRestorePath,
} from '../utils/aiBuilderChatRestore';
import { trackAiBuilderChatRestore } from '../utils/aiBuilderChatTracking';

type UseAiBuilderChatRestoreSessionParams = {
  updateState: (
    updater: (prev: AiBuilderChatState) => AiBuilderChatState,
  ) => void;
  /**
   * 복원을 실제로 적용할 때 1회 호출한다. 스냅샷이 들고 온 id 카운터를 Provider 의
   * ref 에 시드하는 용도 — 복원된 메시지 id 와 이후 새 메시지 id 가 겹치지 않게 한다.
   */
  onRestored: (snapshot: AiBuilderChatRestoreSnapshot) => void;
};

/**
 * 로그인 만료 후 되돌아온 대화를 복원한다.
 *
 * draft token 을 스냅샷에서 이어받는 것이 이 훅의 핵심이다. 서버는
 * `draft_instance_id` 를 포함해 세션 바인딩을 통째로 비교하므로, 마운트마다 새
 * token 을 만들면 복원한 `session_id` 로 보내는 모든 요청이 403 이 된다. 그때 화면에는
 * 말풍선이 그대로 보여 정상처럼 착각하게 되고, 다음 메시지를 보내는 순간 실패한다.
 *
 * 실제 복원 시점은 문항 복원("이어 만들기") 결정을 따른다 — 결합 근거는
 * `aiBuilderChatRestoreDecision` 참고.
 */
export const useAiBuilderChatRestoreSession = ({
  updateState,
  onRestored,
}: UseAiBuilderChatRestoreSessionParams) => {
  const draftInstanceIdRef = useRef<string | null>(null);
  const pendingRestoreRef = useRef<AiBuilderChatRestoreSnapshot | null>(null);
  /** 결정에 닿지 못하고 탈락한 읽기 결과 — 마운트 effect 에서 1회 보고한다. */
  const rejectedReadRef = useRef<AiBuilderChatRestoreReadResult | null>(null);
  const rejectedReadReportedRef = useRef(false);
  const restoreDecision = useAiBuilderChatRestoreDecision();

  if (draftInstanceIdRef.current === null) {
    const read = readAiBuilderChatRestoreSnapshot({
      path: resolveAiBuilderChatRestorePath(),
      now: Date.now(),
    });

    if (read.status === 'ready') {
      pendingRestoreRef.current = read.snapshot;
    } else {
      rejectedReadRef.current = read;
    }

    draftInstanceIdRef.current =
      pendingRestoreRef.current?.draftInstanceId ??
      createAiBuilderDraftInstanceId();
  }

  /**
   * 되살릴 수 없는 스냅샷의 폐기를 계측한다. 발화는 render 가 아니라 effect 에서 —
   * render 는 여러 번 돌 수 있다.
   *
   * 폐기 대상은 저장소에서도 지운다. 남겨두면 빌더에 들어올 때마다 같은 스냅샷을
   * 다시 집계해 수치를 부풀린다. `path_mismatch` 는 그 경로로 돌아오면 아직
   * 유효하므로 지우지도, 발화하지도 않는다(TTL 이 상한을 잡는다).
   */
  useEffect(() => {
    if (rejectedReadReportedRef.current) return;
    rejectedReadReportedRef.current = true;

    const read = rejectedReadRef.current;
    if (!read) return;

    if (read.status === 'stale') {
      clearAiBuilderChatRestoreSnapshot();
      trackAiBuilderChatRestore({
        result: 'stale',
        sessionId: read.snapshot.sessionId,
        messageCount: read.snapshot.messages.length,
        sourceCount: read.snapshot.sourceRefs.length,
        ageMs: Date.now() - read.snapshot.savedAt,
      });
      return;
    }

    if (read.status === 'invalid') {
      clearAiBuilderChatRestoreSnapshot();
      // 파싱조차 안 되므로 채울 수 있는 축이 없다. -1 은 "알 수 없음" 표식.
      trackAiBuilderChatRestore({
        result: 'version_mismatch',
        sessionId: '',
        messageCount: 0,
        sourceCount: 0,
        ageMs: -1,
      });
    }
  }, []);

  // 소비(삭제)는 수락·거절 어느 쪽이든 실행해 무한 복원 루프를 막는다.
  useEffect(() => {
    if (restoreDecision === 'pending') return;

    const pending = pendingRestoreRef.current;
    if (!pending) return;

    const snapshot = consumeAiBuilderChatRestoreSnapshot({
      path: resolveAiBuilderChatRestorePath(),
      now: Date.now(),
    });
    pendingRestoreRef.current = null;

    // 폐기는 명시적 거절일 때만이다 — 복원 여부는 "거절이 아니면 복원"이 원래 계약이라
    // 계측도 같은 기준을 쓴다. 거절이 아닌데 스냅샷이 사라진 경우(결정 대기 중 TTL 초과,
    // 다른 탭이 먼저 소비)는 유저 의사와 무관한 탈락이라 stale 로 센다.
    const rejected = restoreDecision === 'rejected';
    trackAiBuilderChatRestore({
      result: rejected ? 'discarded' : snapshot ? 'restored' : 'stale',
      sessionId: pending.sessionId,
      messageCount: pending.messages.length,
      sourceCount: pending.sourceRefs.length,
      ageMs: Date.now() - pending.savedAt,
    });

    if (rejected || !snapshot) return;

    // 메시지를 넣기 전에 카운터를 시드한다 — 복원 직후 곧바로 제출이 들어와도
    // 새 id 가 복원된 id 뒤에서 시작하도록.
    onRestored(snapshot);
    updateState(prev => applyAiBuilderChatRestoreSnapshot(prev, snapshot));
  }, [onRestored, restoreDecision, updateState]);

  /**
   * 복원 대기 중에는 새 세션을 만들면 안 된다 — 스냅샷의 session_id 를 못 쓰게 된다.
   * 유효한 스냅샷이 실제로 있을 때만 true 라 평시 경로엔 영향이 없다.
   */
  const hasPendingRestore = useCallback(
    () => pendingRestoreRef.current !== null,
    [],
  );

  return {
    draftInstanceId: draftInstanceIdRef.current,
    hasPendingRestore,
  };
};
